import { useState, useMemo, useCallback } from 'react';
import { isEmpty } from 'lodash';

export function useForm({ initialValues = {}, onSubmit, validators }) {
  const [values, setValues] = useState(initialValues);
  const [dirty, setDirty] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(undefined);
  const [didSucceed, setDidSucceed] = useState(undefined);

  const errors = useMemo(
    () =>
      Object.keys(initialValues).reduce((memo, field) => {
        const fieldValidators = validators[field];
        if (!fieldValidators) {
          return memo;
        }

        for (const validate of fieldValidators) {
          const error = validate(values);
          if (error) {
            memo[field] = error;
            return memo;
          }
        }

        return memo;
      }, {}),
    [initialValues, validators, values]
  );

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();
      event.stopPropagation();
      setIsSubmitting(true);

      if (!isEmpty(errors)) {
        setDirty(
          Object.keys(errors).reduce((memo, key) => {
            return {
              ...memo,
              [key]: true
            };
          }, {})
        );
        setIsSubmitting(false);
        return;
      }

      try {
        await onSubmit(values);
        setDidSucceed(true);
        setIsSubmitting(false);
      } catch (e) {
        setError(e);
        setDidSucceed(false);
        setIsSubmitting(false);
      }
    },
    [errors, onSubmit, values]
  );

  return {
    values,
    errors,
    error,
    didSucceed,
    isSubmitting,
    fieldHasError: field => !!(dirty[field] && errors[field]),
    onFieldChange: (field, value) =>
      setValues({
        ...values,
        [field]: value
      }),
    onSubmit: handleSubmit,
    onFieldBlur: field => {
      setDirty({
        ...dirty,
        [field]: !!values[field]
      });
    },
    resetForm: () => {
      setValues(initialValues);
      setDirty({});
      setError(undefined);
      setDidSucceed(undefined);
    }
  };
}
